/**
 * OneAuth API Client
 *
 * 用于与 OneAuth/Kratos 服务通信的 API 客户端
 * 使用独立的 base URL (VITE_KRATOS_PUBLIC_URL)
 */

const KRATOS_BASE_URL = import.meta.env.VITE_KRATOS_PUBLIC_URL || 'http://localhost:4433';
const ONEAUTH_BASE_URL = import.meta.env.VITE_ONEAUTH_URL || 'http://localhost:8080';

/**
 * 通用请求处理函数
 */
async function request(url, options = {}) {
  const defaultOptions = {
    credentials: 'include', // 重要：携带 Kratos session cookie
    headers: {
      'Accept': 'application/json',
      ...options.headers,
    },
  };

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    defaultOptions.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, { ...defaultOptions, ...options });

  // Kratos 可能返回 422（表单验证错误）或其他状态码
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.error?.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Kratos API 方法
 */
export const kratosApi = {
  /**
   * 创建登录流程
   * @param {string} returnUrl - 登录成功后返回的 URL
   * @returns {Promise} 登录流程对象
   */
  async createLoginFlow(returnUrl) {
    const params = new URLSearchParams({ return_to: returnUrl || window.location.origin });
    return request(`${KRATOS_BASE_URL}/self-service/login/browser?${params}`);
  },

  /**
   * 获取登录流程
   * @param {string} flowId - 流程 ID
   * @returns {Promise} 登录流程对象
   */
  async getLoginFlow(flowId) {
    return request(`${KRATOS_BASE_URL}/self-service/login/flows?id=${flowId}`);
  },

  /**
   * 提交登录表单
   * @param {string} flowId - 流程 ID
   * @param {object} body - 登录数据 (method, identifier, password 等)
   * @returns {Promise} 响应数据
   */
  async submitLogin(flowId, body) {
    return request(`${KRATOS_BASE_URL}/self-service/login?flow=${flowId}`, {
      method: 'POST',
      body,
    });
  },

  /**
   * 创建注册流程
   * @param {string} returnUrl - 注册成功后返回的 URL
   * @returns {Promise} 注册流程对象
   */
  async createRegistrationFlow(returnUrl) {
    const params = new URLSearchParams({ return_to: returnUrl || window.location.origin });
    return request(`${KRATOS_BASE_URL}/self-service/registration/browser?${params}`);
  },

  /**
   * 提交注册表单
   * @param {string} flowId - 流程 ID
   * @param {object} body - 注册数据
   * @returns {Promise} 响应数据
   */
  async submitRegistration(flowId, body) {
    return request(`${KRATOS_BASE_URL}/self-service/registration?flow=${flowId}`, {
      method: 'POST',
      body,
    });
  },

  /**
   * 获取当前会话信息（whoami）
   * @returns {Promise} 会话信息
   */
  async getSession() {
    return request(`${KRATOS_BASE_URL}/sessions/whoami`);
  },

  /**
   * 创建登出流程
   * @returns {Promise} 登出 URL 和 token
   */
  async createLogoutFlow() {
    return request(`${KRATOS_BASE_URL}/self-service/logout/browser`);
  },

  /**
   * 执行登出
   * @param {string} logoutToken - 登出 token
   * @returns {Promise}
   */
  async logout(logoutToken) {
    return request(`${KRATOS_BASE_URL}/self-service/logout?token=${logoutToken}`, {
      method: 'GET',
    });
  },

  /**
   * 创建恢复流程（忘记密码）
   * @returns {Promise} 恢复流程对象
   */
  async createRecoveryFlow() {
    return request(`${KRATOS_BASE_URL}/self-service/recovery/browser`);
  },

  /**
   * 创建验证流程（邮箱验证）
   * @returns {Promise} 验证流程对象
   */
  async createVerificationFlow() {
    return request(`${KRATOS_BASE_URL}/self-service/verification/browser`);
  },

  /**
   * 创建设置流程（修改密码等）
   * @returns {Promise} 设置流程对象
   */
  async createSettingsFlow() {
    return request(`${KRATOS_BASE_URL}/self-service/settings/browser`);
  },

  /**
   * 提交设置更新
   * @param {string} flowId - 流程 ID
   * @param {object} body - 更新数据
   * @returns {Promise} 响应数据
   */
  async submitSettings(flowId, body) {
    return request(`${KRATOS_BASE_URL}/self-service/settings?flow=${flowId}`, {
      method: 'POST',
      body,
    });
  },
};

/**
 * OneAuth Native OAuth API 方法（小程序用）
 */
export const nativeOAuthApi = {
  /**
   * 微信小程序登录
   * @param {object} params - 登录参数
   * @returns {Promise} Token 响应
   */
  async wechatLogin(params) {
    return request(`${ONEAUTH_BASE_URL}/oauth/native/wechat`, {
      method: 'POST',
      body: params,
    });
  },

  /**
   * 验证 session
   * @param {string} accessToken - Access token
   * @returns {Promise} Session 信息
   */
  async getSession(accessToken) {
    return request(`${ONEAUTH_BASE_URL}/oauth/native/session`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  },
};

/**
 * Token 验证 API（业务后端使用）
 */
export const tokenApi = {
  /**
   * 验证 token
   * @param {string} token - Token
   * @returns {Promise} 验证结果
   */
  async verify(token) {
    return request(`${ONEAUTH_BASE_URL}/oauth/token/verify`, {
      method: 'POST',
      body: { token },
    });
  },

  /**
   * Token introspection
   * @param {string} token - Token
   * @returns {Promise} Introspection 结果
   */
  async introspect(token) {
    return request(`${ONEAUTH_BASE_URL}/oauth/token/introspect`, {
      method: 'POST',
      body: { token },
    });
  },
};

export default {
  kratos: kratosApi,
  native: nativeOAuthApi,
  token: tokenApi,
};
